import { useEffect } from 'react';
import { LogOut, Sun, Moon, Loader2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/infra/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuthStore } from '@/stores/authStore';
import { startBuildWatch, useBuildUpdateStore } from '@/stores/buildUpdateStore';
import { pathMatches, type NavGroup } from '@/types/navigation';
import { BuildUpdateBreakout } from './BuildUpdateBreakout';
import { NavTransitionProvider, NavTimeoutBanner, TransitionLink, useNavSpinner } from './NavTransition';
import { StatusBar } from './StatusBar';

interface AuthShellProps {
  groups: NavGroup[];
  children: React.ReactNode;
  brandLetter?: string;
}

// One divider style for the whole rail — brand↔nav, between sections, and above
// the footer. Prominent on purpose (the old border-border line was too faint).
const SIDEBAR_DIVIDER = 'bg-foreground/30 mx-2 my-2.5 h-0.5 rounded-full';

// The provider has to sit above both the rail and children — TabShell renders
// inside children (App puts the route tree here), and both shells read the same
// pending navigation.
export function AuthShell(props: AuthShellProps) {
  return (
    <NavTransitionProvider>
      <AuthShellLayout {...props} />
    </NavTransitionProvider>
  );
}

function AuthShellLayout({ groups, children, brandLetter = 'K' }: AuthShellProps) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const currentView = useLocation().pathname.split('/')[1] || '';

  const mainGroups = groups.filter((g) => !g.footer);
  const footerGroups = groups.filter((g) => g.footer);

  // The shell is the one place mounted for the whole session, so the build
  // watch starts here — the three affordances only subscribe. Refcounted, so
  // StrictMode's mount/unmount/mount tears the poller down and restarts it
  // cleanly instead of stacking a second one.
  useEffect(() => startBuildWatch(), []);

  let lastSection: string | undefined;

  return (
    <div data-view={currentView} className="bg-background text-foreground flex h-screen overflow-hidden">
      {/* Sidebar. Dark mode: bg-muted (a lighter step than the page) carries the
          separation. Light mode: near-white surfaces can't separate by fill, so the
          rail goes white + a faint right-edge shadow to read as the most-forward
          surface (elevation, not a darker fill) on the 96% canvas. */}
      <aside className="bg-muted light:bg-card light:shadow-[1px_0_4px_0_rgba(0,0,0,0.05)] border-border/50 w-16 border-r">
        <div className="flex h-full flex-col">
          {/* Brand + identity — letter over username. Separated from the nav by a
              divider (not a background tint) — same divider used between sections
              and above the footer so the whole rail reads consistently. */}
          <div className="relative flex h-14 flex-col items-center justify-center gap-0.5">
            <span className="text-primary text-lg font-bold leading-none">{brandLetter}</span>
            {user?.username && (
              <span className="text-muted-foreground max-w-[3.25rem] truncate text-[9px] leading-none" title={user.username}>
                {user.username}
              </span>
            )}
            <BuildUpdatePip />
          </div>
          <div className={SIDEBAR_DIVIDER} />

          {/* Nav. The first group never gets a section divider (lastSection starts
              undefined), so the brand divider above is never doubled — true for
              every role, including traders whose first section is 'trade'. */}
          <nav className="flex-1 py-2">
            {mainGroups.map((group) => {
              const showDivider = lastSection !== undefined && group.section !== lastSection;
              lastSection = group.section;
              return (
                <div key={group.id}>
                  {showDivider && <div className={SIDEBAR_DIVIDER} />}
                  <NavLinkButton group={group} />
                </div>
              );
            })}
          </nav>

          {/* Footer: settings, theme, logout */}
          <div className={SIDEBAR_DIVIDER} />
          <div className="space-y-1 py-2">
            {footerGroups.map((group) => (
              <NavLinkButton key={group.id} group={group} />
            ))}
            <ThemeButton />
            <button
              onClick={() => logout()}
              title="Sign out"
              className={cn(
                'group relative flex h-12 w-full items-center justify-center transition-colors',
                'text-muted-foreground hover:text-destructive hover:bg-destructive/10',
              )}
            >
              <LogOut className="h-5 w-5" />
              <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
                Sign out
              </div>
            </button>
          </div>
        </div>
      </aside>
      <main className="flex flex-1 flex-col overflow-hidden">
        {/* Above the routed content and outside it, so a stalled navigation to
            any route surfaces here over the still-visible outgoing page. */}
        <NavTimeoutBanner />
        <div className="flex-1 overflow-hidden">{children}</div>
        <StatusBar />
        {/* Fixed-position, so it overlays rather than resizing the shell — the
            content pane's height must not move when a deploy lands. */}
        <BuildUpdateBreakout />
      </main>
    </div>
  );
}

// Single sidebar nav button. Active state is derived from the URL (not props) so
// browser back/forward, deep links, and "open in new tab" all just work. A group
// with >1 page renders the icon (→ its default page) plus a hover flyout listing
// the pages; each is a direct link, so you can jump straight to any tab.
function NavLinkButton({ group }: { group: NavGroup }) {
  const Icon = group.icon;
  const { pathname } = useLocation();
  const spinnerPath = useNavSpinner();
  const isActive = group.pages.some((p) => pathMatches(pathname, p.path));
  const defaultPath = group.pages[0]?.path ?? '/';
  const grouped = group.pages.length > 1;
  // Spin for any pending page in this group, not just defaultPath — a click in
  // the flyout should light up the rail icon the user is heading toward too.
  const pending = spinnerPath !== null && group.pages.some((p) => p.path === spinnerPath);

  const iconInner = (
    <>
      {/* Swap in place at identical h-5 w-5: the rail must not reflow mid-nav. */}
      {pending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />}
      {group.badge && (
        <span className="bg-muted-foreground/20 text-muted-foreground absolute bottom-1 right-1 rounded px-0.5 text-[7px] font-semibold uppercase leading-tight">
          {group.badge}
        </span>
      )}
      {isActive && <div className="bg-primary absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-r" />}
    </>
  );
  const iconClasses = cn(
    'relative flex h-12 w-full items-center justify-center transition-colors',
    !isActive && 'text-muted-foreground group-hover:text-foreground',
    isActive && 'text-primary bg-primary/10',
  );

  // Grouped: icon + hover flyout of pages (links are siblings, never nested).
  if (grouped) {
    return (
      <div className="group relative">
        <TransitionLink to={defaultPath} data-nav-button={group.id} title={group.label} className={cn(iconClasses, !isActive && 'group-hover:bg-muted/50')}>
          {iconInner}
        </TransitionLink>
        <div className="invisible absolute left-full top-0 z-50 pl-2 opacity-0 transition-all group-hover:visible group-hover:opacity-100">
          <div className="border-border bg-popover min-w-[172px] rounded-md border py-1 shadow-lg">
            <div className="text-muted-foreground px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wide">
              {group.label}
            </div>
            {group.pages.map((p) => {
              const tabActive = pathMatches(pathname, p.path);
              return (
                <TransitionLink
                  key={p.id}
                  to={p.path}
                  className={cn(
                    'flex items-center justify-between gap-3 px-3 py-1.5 text-xs',
                    tabActive ? 'text-primary bg-primary/10' : 'text-foreground/80 hover:bg-muted/60 hover:text-foreground',
                  )}
                >
                  <span className="flex items-center gap-1.5">
                    {p.label}
                    {spinnerPath === p.path && <Loader2 className="h-3 w-3 animate-spin" />}
                  </span>
                  {p.badge && (
                    <span className="bg-muted-foreground/20 text-muted-foreground rounded px-1 text-[8px] font-semibold uppercase">
                      {p.badge}
                    </span>
                  )}
                </TransitionLink>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Single page: icon + a simple label tooltip.
  return (
    <TransitionLink to={defaultPath} data-nav-button={group.id} title={group.label} className={cn('group', iconClasses, !isActive && 'hover:bg-muted/50', 'hover:text-foreground')}>
      {iconInner}
      <div className="bg-popover text-popover-foreground invisible absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
        {group.label}
      </div>
    </TransitionLink>
  );
}

/**
 * The persistent header-side half of the new-build prompt. This shell has no
 * horizontal header — the rail's brand block is its top-of-shell region — so
 * the indicator lives there, absolutely positioned inside the fixed-height
 * block so it can never reflow the rail.
 *
 * Unlike the breakout, this survives Dismiss: it stays until the tab reloads
 * into the newer build. Clicking it brings the breakout back, which is what
 * makes Dismiss safe to press.
 */
function BuildUpdatePip() {
  const updateAvailable = useBuildUpdateStore((s) => s.updateAvailable);
  const showBreakout = useBuildUpdateStore((s) => s.showBreakout);
  if (!updateAvailable) return null;

  return (
    <button
      type="button"
      onClick={showBreakout}
      data-testid="build-update-pip"
      aria-label="A new version of g1 is available"
      // No `title`: aria-label already names it and the hover card below is the
      // visual tooltip, so a native one would only race it with different words.
      // The padding is the point — it lifts an 8px dot to a ~24px hit target
      // without changing how the dot looks at rest.
      className="group absolute right-0.5 top-1 p-2"
    >
      {/* The ring separates the dot from the rail fill in both themes. */}
      <span className="ring-muted light:ring-card block h-2 w-2 animate-pulse rounded-full bg-amber-500 ring-2" />
      <div className="bg-popover text-popover-foreground invisible absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
        New version available
      </div>
    </button>
  );
}

function ThemeButton() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      onClick={toggleTheme}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={cn(
        'group relative flex h-12 w-full items-center justify-center transition-colors',
        'text-muted-foreground hover:text-foreground hover:bg-muted/50',
      )}
    >
      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
        {isDark ? 'Light mode' : 'Dark mode'}
      </div>
    </button>
  );
}
